import { useEffect } from "react";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "@components/Buttons";
import useEscapeKey from "@hooks/useEscapeKey";
import { formatSbomApiError } from "@utils/error";

const ErrorDetailsModal = ({
  isOpen,
  onClose,
  errors = [],
  size = "lg",
  className = "",
  showCloseButton = true,
  "data-testid": dataTestId = "error-detail-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();
  const { t, i18n } = useTranslation();
  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-full mx-4",
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px]`}
      data-testid={`${dataTestId}-overlay`}
    >
      <div
        className={`relative w-[40rem] ${colors.bgModal} border ${colors.borderPrimary} transform overflow-hidden rounded-lg shadow-xl transition-all duration-200 ${sizeClasses[size]} ${className}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={dataTestId}
      >
        {/* ================= HEADER ================= */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2" data-testid={`${dataTestId}-header`}>
          <h2 className={`text-xl font-bold ${colors.textPrimary} uppercase`} data-testid={`${dataTestId}-title`}>
            {t("common.error-detail")}
          </h2>
          {showCloseButton && (
            <button
              data-testid={`${dataTestId}-close-button`}
              onClick={onClose}
              className={`transition-colors duration-200 ${colors.iconMuted} ${colors.hoverTextSecondary}`}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* ================= CONTENT ================= */}
        <div className="px-6 pb-6 overflow-y-auto flex-1" data-testid={`${dataTestId}-content`}>
          {errors?.length > 0 ? (
            <ul 
              className={`space-y-3 ${colors.textPrimary} ${colors.bgInput} ${colors.borderPrimary} w-full border focus:outline-none transition-colors overflow-y-auto`}
              data-testid={fieldContainerTestId("errors")}
              style={{
                fontSize: "0.875rem",
                padding: "0.375rem 0.625rem",
                borderRadius: "0.5rem",
                maxHeight: "40vh",
              }}
            >
              {errors.map((error, index) => {
                let text;
                if (typeof error === "string") {
                  text = error;
                } else if (error?.messageKey) {
                  text = formatSbomApiError(t, error, i18n.language) || t(error.messageKey);
                } else {
                  text = JSON.stringify(error, null, 2);
                }
                return (
                  <li key={index}>
                    <pre className="mt-1 whitespace-pre-wrap break-words">{text}</pre>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className={`text-center ${colors.textMuted}`} data-testid={fieldValueTestId("errors")}>
              No error details available.
            </div>
          )}
        </div>

        {/* ================= FOOTER ================= */}
        <div className={`flex items-center justify-end space-x-3 border-t px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <PrimaryButton
            data-testid={`${dataTestId}-ok-button`}
            onClick={onClose}
            size="md"
          >
            {t("common.ok-button")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default ErrorDetailsModal;
